import { FC, useCallback, useMemo, useState } from "react";

import { PreclassCheckResultType } from "../../../../../shared/types/preclass-check";

import { SystemCheckResultsData } from "api/api-data-types";
import { serverGetSystemCheckResults } from "api/api-server-data";
import { PreclassCheckMessages } from "components/av/preclass-check-messages";
import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { usePromiseEffect } from "store/store-hooks";

type SystemChecksTableProps = {
  data: SystemCheckResultsData[];
};

const SystemChecksTable: FC<SystemChecksTableProps> = ({ data }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString();
  };
  const getTestErrorMessage = (testResults: PreclassCheckResultType) => {
    try {
      const message = PreclassCheckMessages.getHighestPriorityMessageFromResults(testResults);
      return message.message || "";
    } catch {
      return "";
    }
  };
  return (
    <div className="overflow-x-auto">
      <div className="max-h-[330px] overflow-y-auto">
        <table className="mds-table w-full">
          <thead className="sticky top-0 z-10 bg-white shadow-sm">
            <tr>
              <th className="w-[120px]">{t("user_info.name")}</th>
              <th className="w-[160px]">{t("user_info.email")}</th>
              <th className="w-[120px]">{t("user_info.created_date")}</th>
              <th className="w-[120px]">{t("av.system_check.report.check_date")}</th>
              <th className="w-[90px]">{t("av.system_check.report.IP")}</th>
              <th className="w-[120px]">{t("av.system_check.report.browser")}</th>
              <th className="w-[120px]">{t("av.system_check.report.OS")}</th>
              <th className="w-[90px]">{t("av.system_check.report.outcome")}</th>
              <th className="w-[200px]">{t("av.system_check.report.error")}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.created_at + row.user_email + row.ip}>
                <td className="w-[120px] truncate pr-[1px]">{row.user_full_name}</td>
                <td className="w-[160px] truncate pr-[1px]">{row.user_email}</td>
                <td className="w-[120px] truncate pr-[1px]">{formatDate(row.user_created_at)}</td>
                <td className="w-[120px] truncate pr-[1px]">{formatDate(row.created_at)}</td>
                <td className="w-[90px] truncate pr-[1px]">{row.ip}</td>
                <td className="w-[120px] truncate pr-[1px]">{row.browser || "-"}</td>
                <td className="w-[120px] truncate pr-[1px]">{row.os || "-"}</td>
                <td className="w-[90px] truncate pr-[1px]">{row.check_end_result}</td>
                <td className="w-[200px] truncate pr-[1px]">
                  {getTestErrorMessage(row.test_results)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const SystemChecksReport: FC = () => {
  const [onlyShowFailed, setOnlyShowFailed] = useState(false);

  type DateRangeOption = {
    id: string;
    title: string;
    value: number;
  };

  const DATE_RANGE_OPTIONS: DateRangeOption[] = [
    { id: "none", title: t("common.date_range.select_time_period"), value: 0 },
    { id: "7", title: t("common.date_range.last_7_days"), value: 7 },
    { id: "30", title: t("common.date_range.last_30_days"), value: 30 },
    { id: "all", title: t("common.date_range.all_time"), value: -1 },
  ];

  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>(
    DATE_RANGE_OPTIONS.find((option) => option.id === "none"),
  );

  // Calculate date range based on selected option
  const getStartDate = useCallback(() => {
    if (selectedDateRange.value === -1) {
      return "2020-01-01T00:00:00Z";
    }
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - selectedDateRange.value);
    return startDate.toISOString();
  }, [selectedDateRange.value]);

  const { data, isLoading, error } = usePromiseEffect(async () => {
    const startDate = getStartDate();

    if (!selectedDateRange || selectedDateRange.id === "none") {
      return [];
    }
    return serverGetSystemCheckResults(startDate);
  }, [selectedDateRange, getStartDate]);

  const filteredResults = useMemo(() => {
    return !data ? [] : data.filter((row) => !onlyShowFailed || row.check_end_result === "failed");
  }, [data, onlyShowFailed]);

  return (
    <div className="system-checks-report-view">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("av.system_check.report.title")}</h2>
      </div>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div>
          <DropdownMenu
            className="mds-input w-60"
            eventName="SystemChecksReport / Time period selected"
            eventPropertyKey="timePeriod"
            items={DATE_RANGE_OPTIONS}
            openMenuEventName="SystemChecksReport / Open date range menu"
            selectedValue={selectedDateRange.id}
            size="s"
            title={t("common.date_range.select_time_period")}
            onSelectionChange={(item) => setSelectedDateRange(item)}
          />
        </div>
        <div className="flex h-10 items-center">
          <Checkbox
            checked={onlyShowFailed}
            className="mr-2 mt-[6px]"
            id="only-show-failed"
            onChange={(checked) => setOnlyShowFailed(checked)}
          >
            {t("av.system_check.report.only_failed_tests")}
          </Checkbox>
        </div>
      </div>
      {error && <div className="bg-red-50 text-red-700 mb-4 rounded-md p-4">{error.message}</div>}
      {isLoading && (
        <div className="mt-8 text-center text-black-tint-40">{t("common.loading_ellipsis")}</div>
      )}
      {filteredResults && <SystemChecksTable data={filteredResults} />}
      {filteredResults && filteredResults.length === 0 && !error && (
        <div className="mt-8 text-center text-black-tint-40">
          {t("av.system_check.report.select_date_to_view_data")}
        </div>
      )}
    </div>
  );
};
